import {
  AppBar,
  Button,
  Grid,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { DeleteDialog, HeadTable, Layout, TableCaption } from 'components';
import { ButtonDelete, ButtonEdit } from 'components/common/table';
import { drawerWidth } from 'config';
import { getSortItem } from 'helpers';
import { useDateTime } from 'hooks';
import { DELETE_DIALOG_TYPE, TYPE_POPUP } from 'models';
import {
  PUBLIC_HOLIDAY_ITEM_TYPE,
  PUBLIC_HOLIDAY_LIST_PARAMS_REQUEST,
} from 'models/public-holiday';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setPublicHolidayListParams } from 'store/reducers/public-holiday';
import { getPublicHolidayListRequest } from 'store/reducers/public-holiday/actionType';
import { PopupCreateEditPH } from './components';
import { headCells } from './data';

const PublicHolidayPage = () => {
  const dispatch = useAppDispatch();
  const { formatDateTime } = useDateTime();
  const { list: public_holiday_list } = useAppSelector(
    (state) => state.publicHolidayReducer.publicHoliday
  );
  const { params } = public_holiday_list;

  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [publicHolidayItem, setPublicHolidayItem] = useState<
    PUBLIC_HOLIDAY_ITEM_TYPE | undefined
  >();

  const onSetListParams = (
    params: PUBLIC_HOLIDAY_LIST_PARAMS_REQUEST | null
  ) => {
    dispatch(setPublicHolidayListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getPublicHolidayListRequest(params));
    }
  }, [params, dispatch]);
  const setOpenCreate = () => {
    setOpenCreatePopup(true);
  };

  const setOpenEdit = (event: any, data: PUBLIC_HOLIDAY_ITEM_TYPE) => {
    event.stopPropagation();
    setPublicHolidayItem({
      ...data,
      date: `${data.date}-${data.year ?? new Date().getFullYear()}`,
    });
    setOpenEditPopup(true);
  };

  const setOpenDelete = (event: any, data: PUBLIC_HOLIDAY_ITEM_TYPE) => {
    event.stopPropagation();
    setPublicHolidayItem({
      ...data,
      date: `${data.date} ${data.year ?? new Date().getFullYear()}`,
    });
    setOpenDeletePopup(true);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: public_holiday_list.metadata.total_pages,
    page: public_holiday_list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setPublicHolidayItem(undefined);
        break;
      case TYPE_POPUP.EDIT:
        setOpenEditPopup(false);
        setPublicHolidayItem(undefined);
        break;
      case TYPE_POPUP.CREATE_OR_EDIT:
        setOpenCreatePopup(false);
        setPublicHolidayItem(undefined);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getPublicHolidayListRequest(params));
  };

  const sortItem = useMemo(() => {
    return getSortItem(public_holiday_list.params?.sort);
  }, [public_holiday_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof PUBLIC_HOLIDAY_ITEM_TYPE
  ) => {
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const getDateString = (dateString: string) => {
    const dateSplit = dateString.split('-');

    const monthMap = {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mar',
      '04': 'Apr',
      '05': 'May',
      '06': 'Jun',
      '07': 'Jul',
      '08': 'Aug',
      '09': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    };

    const month = monthMap[dateSplit[0] as keyof typeof monthMap];
    return `${dateSplit[1]} ${month}`;
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Public Holiday
            </Typography>
          </Grid>
          <Stack spacing={1} direction="row">
            <Button
              variant="contained"
              color="secondary"
              sx={{
                height: 30,
                width: '72px',
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '100%',
              }}
              onClick={setOpenCreate}
            >
              Create
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<PUBLIC_HOLIDAY_ITEM_TYPE>
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={public_holiday_list.records.length}
              headCells={headCells}
              onCheckbox={false}
              onRequestSort={handleRequestSort}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {public_holiday_list.records.map((row, index) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id ?? index}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.name}
                    </TableCell>

                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {getDateString(row?.date)}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.year ?? new Date().getFullYear()}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row?.createdAt)}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      <Grid
                        container
                        alignItems="center"
                        textTransform="capitalize"
                      >
                        <Grid
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            marginRight: '5px',
                            backgroundColor: row.is_active
                              ? 'green.light'
                              : 'red.light',
                          }}
                        />
                        <Typography>
                          {row?.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Grid>
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                        <ButtonDelete onClick={(e) => setOpenDelete(e, row)} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={public_holiday_list.metadata}
          items={items}
        />
      </Stack>
      {openDeletePopup && (
        <DeleteDialog
          name={publicHolidayItem?.id}
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={publicHolidayItem?.id}
          onSuccess={onReloadList}
          type={DELETE_DIALOG_TYPE.PUBLIC_HOLIDAY}
        />
      )}
      {openCreatePopup && (
        <PopupCreateEditPH
          open={true}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.CREATE_OR_EDIT)}
        />
      )}
      {openEditPopup && (
        <PopupCreateEditPH
          open
          subject={publicHolidayItem}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.EDIT)}
        />
      )}
    </Layout>
  );
};

export default PublicHolidayPage;
